import React, { useEffect, useRef, useState } from "react";
import { useLanguage } from "../../i18n/LanguageContext";

type FaqItem = {
  question: string;
  answer: string;
};

const ServicesFaqSection: React.FC = () => {
  const { t, dir } = useLanguage();
  const sectionRef = useRef<HTMLElement | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [openIndex, setOpenIndex] = useState(0);
  const isRtl = dir === "rtl";

  const content = (t as any)?.servicesFaq || {};
  const items = (content.items || []) as FaqItem[];

  useEffect(() => {
    const element = sectionRef.current;
    if (!element) return;

    if (!("IntersectionObserver" in window)) {
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      { rootMargin: "0px 0px -10% 0px", threshold: 0.12 }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="bg-white px-4 py-16 sm:px-8 sm:py-20 lg:px-16 lg:py-24"
      aria-labelledby="services-faq-title"
      dir={isRtl ? "rtl" : "ltr"}
    >
      <div
        className={`grid gap-8 lg:gap-8 ${
          isRtl
            ? "lg:grid-cols-[minmax(0,1fr)_440px]"
            : "lg:grid-cols-[440px_minmax(0,1fr)]"
        }`}
      >
        <div
          className={`lg:sticky lg:top-28 lg:self-start ${
            isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"
          } transition-all duration-700 ease-out ${isRtl ? "lg:order-2" : ""}`}
        >
          <div
            className={`flex flex-col gap-3 ${
              isRtl ? "items-end text-right" : "items-start text-left"
            }`}
          >
            <h2
              id="services-faq-title"
              className="font-sans text-[1.7rem] font-semibold uppercase leading-tight text-teal-900 sm:text-[28px] sm:leading-[1.2]"
            >
              {content.title || "Common questions from parents and students."}
            </h2>
            <p className="font-sans text-sm font-normal leading-6 text-neutral-600 sm:text-[15px]">
              {content.description ||
                "Before you start the process, it's natural to have a lot of questions. Here are the ones we hear most often with straightforward answers from our admissions team."}
            </p>
          </div>
        </div>

        <div className={`flex flex-col gap-4 ${isRtl ? "lg:order-1" : ""}`}>
          {items.map((item, index) => {
            const isOpen = openIndex === index;

            return (
              <article
                key={item.question}
                className={`overflow-hidden rounded-xl border border-zinc-100 bg-white transition-all duration-500 ease-out ${
                  isVisible
                    ? "translate-y-0 opacity-100"
                    : "translate-y-8 opacity-0"
                }`}
                style={{ transitionDelay: `${index * 50}ms` }}
              >
                <button
                  type="button"
                  onClick={() => setOpenIndex((current) => (current === index ? -1 : index))}
                  aria-expanded={isOpen}
                  className={`flex w-full items-center gap-5 px-5 py-5 text-left sm:px-8 sm:py-6 ${
                    isRtl ? "flex-row-reverse text-right" : "text-left"
                  }`}
                >
                  <span className="flex-1 font-sans text-base font-medium leading-7 text-neutral-800 sm:text-lg">
                    {item.question}
                  </span>
                  <span className="flex h-[52px] w-[52px] shrink-0 items-center justify-center rounded-lg bg-main-color/20 text-neutral-800">
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      aria-hidden="true"
                      className={`transition-transform duration-300 ${
                        isOpen ? "rotate-45" : "rotate-0"
                      }`}
                    >
                      <path
                        d="M10 4V16M4 10H16"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                </button>

                <div
                  className={`grid transition-[grid-template-rows,opacity] duration-500 ease-out ${
                    isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
                  }`}
                >
                  <div className="overflow-hidden">
                    <div
                      className={`border-t border-zinc-100 px-5 py-5 font-sans text-sm font-normal leading-7 text-neutral-600 sm:px-8 sm:py-6 sm:text-base ${
                        isRtl ? "text-right" : "text-left"
                      } whitespace-pre-line`}
                    >
                      {item.answer}
                    </div>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ServicesFaqSection;
